import { ManhwaRecap, Channel } from '../types';
import { profiler } from '../utils/profiler';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api';

export class ApiService {
  private static async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const profilerName = `api.request.${endpoint.split('?')[0].replace(/\//g, '_')}`;

    profiler.start(profilerName, { url, method: options?.method || 'GET' });

    let profilerEnded = false;
    const safeEndProfiler = (metadata?: Record<string, any>) => {
      if (!profilerEnded) {
        profiler.end(profilerName, metadata);
        profilerEnded = true;
      }
    };

    try {
      const fetchStartTime = performance.now();
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      const fetchDuration = performance.now() - fetchStartTime;

      // Parse response
      const parseStartTime = performance.now();
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // JSON parsing failed
        safeEndProfiler({
          success: false,
          status: response.status,
          fetchDuration,
          parseDuration: performance.now() - parseStartTime,
          error: 'JSON parsing failed'
        });
        console.error('JSON parsing failed:', parseError);
        throw new Error('Invalid response format from server');
      }
      const parseDuration = performance.now() - parseStartTime;

      if (!response.ok) {
        const errorMessage = data?.message || `HTTP error! status: ${response.status}`;
        safeEndProfiler({
          success: false,
          status: response.status,
          fetchDuration,
          parseDuration,
          error: errorMessage
        });
        console.log(`API request failed with status ${response.status}:`, errorMessage);
        throw new Error(errorMessage);
      }

      safeEndProfiler({
        success: true,
        status: response.status,
        fetchDuration,
        parseDuration,
        totalDuration: fetchDuration + parseDuration
      });

      return data;
    } catch (error) {
      // Only end profiler if it hasn't been ended already
      if (error.message !== 'Invalid response format from server') {
        safeEndProfiler({
          success: false,
          error: error instanceof Error ? error.message : String(error)
        });
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  public static async getRecaps(filters: any = {}): Promise<{ data: ManhwaRecap[], total: number, hasMore: boolean }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      // Include series_name even if it's null (backend will handle it)
      if (value !== undefined && value !== '' && value !== null) {
        if (key === 'channelId') {
          params.append('channel_id', String(value));
        } else if (key === 'series_name') {
          // Properly encode series_name for URL (handles special characters like ?, &, etc.)
          params.append('series_name', encodeURIComponent(String(value)));
        } else {
          // Properly encode the value to handle special characters like ?, &, etc.
          params.append(key, String(value));
        }
      } else if (key === 'series_name' && value !== undefined && value !== null) {
        // Special case: send series_name even if empty string (let backend decide)
        params.append('series_name', String(value));
      }
    });
    // Debug log in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 API: Filters received:', filters);
      console.log('🔍 API: URL params:', params.toString());
      if (filters.series_name) {
        console.log('🔍 API: Sending series_name:', filters.series_name);
      }
    }
    return this.request(`/recaps?${params}`);
  }

  public static async getRecapsByIds(ids: string[]): Promise<ManhwaRecap[]> {
    if (ids.length === 0) {
      return [];
    }
    const params = new URLSearchParams();
    params.append('ids', ids.join(','));
    const result = await this.request<{ data: ManhwaRecap[] }>(`/recaps?${params}`);
    return result.data;
  }

  public static async getRecapById(id: string): Promise<ManhwaRecap> {
    return this.request(`/recaps/${id}`);
  }

  public static async getChannels(filters: any = {}): Promise<{ data: Channel[], total: number, hasMore: boolean }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    return this.request(`/channels?${params}`);
  }

  public static async updateRecap(recapId: string, data: { series_name?: string; status?: string; thumbnail_url?: string }): Promise<any> {
    return this.request(`/recaps/${recapId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public static async getLanguages(): Promise<string[]> {
    return this.request('/recaps/languages');
  }

  public static async rateRecap(recapId: string, rating: number): Promise<any> {
    return this.request(`/recaps/${recapId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
  }

  public static async getStats(): Promise<any> {
    return this.request('/stats');
  }

  public static async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request(`/auth/verify-email?token=${token}`);
  }

  public static async getCurrentUser(): Promise<any> {
    return this.request('/auth/me');
  }

  public static async logout(): Promise<any> {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  public static async deactivateChannel(channelId: number): Promise<any> {
    return this.request(`/channels/${channelId}/deactivate`, {
      method: 'PUT',
    });
  }

  // Favourites methods
  public static async getFavouriteChannels(filters: any = {}): Promise<{ data: Channel[], total: number, hasMore: boolean }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });
    return this.request(`/channels/favourites?${params}`);
  }

  public static async addToFavourites(channelId: number): Promise<any> {
    return this.request(`/channels/${channelId}/favourite`, {
      method: 'POST',
    });
  }

  public static async removeFromFavourites(channelId: number): Promise<any> {
    return this.request(`/channels/${channelId}/favourite`, {
      method: 'DELETE',
    });
  }

  public static async checkFavouriteStatus(channelId: number): Promise<{ isFavourited: boolean }> {
    return this.request(`/channels/favourites/check/${channelId}`);
  }

  // Admin methods
  public static async getPendingChannels(): Promise<any[]> {
    return this.request('/admin/pending-channels');
  }

  public static async approveChannel(channelId: number, data: any): Promise<any> {
    return this.request(`/admin/pending-channels/${channelId}/approve`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public static async rejectChannel(channelId: number, data: any): Promise<any> {
    return this.request(`/admin/pending-channels/${channelId}/reject`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Series list methods
  public static async getSeriesList(startsWith?: string, page: number = 1, limit: number = 60): Promise<{
    data: { series_name: string, recap_count: number, series_thumbnail?: string | null }[],
    total: number,
    page: number,
    limit: number,
    hasMore: boolean
  }> {
    const params = new URLSearchParams();
    if (startsWith) {
      params.append('startsWith', startsWith);
    }
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return this.request(`/recaps/series/list?${params}`);
  }

  public static async getSeriesByGenre(genre: string, page: number = 1, limit: number = 60): Promise<{
    data: { series_name: string, recap_count: number, series_thumbnail?: string }[],
    total: number,
    page: number,
    limit: number,
    hasMore: boolean
  }> {
    const params = new URLSearchParams();
    params.append('genre', genre);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    return this.request(`/recaps/series/by-genre?${params}`);
  }
}